import { FC, useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import RText from "../../../RText/RText";
import variables from "../../../../styles/variables";
import RTextInput from "../../Account/components/RTextInput";
import { useAtom } from "jotai";
import { openAuthModalAtom } from "../../../../atoms";
import { getTrackedHorsesByIds } from "../../../../client/tracker";
import { useToast } from "react-native-toast-notifications";
import { TextLoader } from "../../../States/Loading/Loaders";
import { useRoute } from "@react-navigation/native";
import useUser from "../../../../hooks/useUser";

import { HorseTrackerType } from "../../../../types/races/horse";
import TrackerButton from "./TrackerButton";
import Gap from "../../../Gap";
import { handleDeleteTrackedHorse, handleUpdateTrackedHorse } from "../utils";

type TrackedHorseCommentProps = {
  horseId: number;
  horseName: string;
  isExpanded: boolean;
  withControls: boolean;
  trackedHorse?: HorseTrackerType;
};

export const TrackedHorseComment: FC<TrackedHorseCommentProps> = ({
  horseId,
  horseName,
  isExpanded = false,
  withControls = true,
  trackedHorse = false,
}) => {
  const toast = useToast();
  const [, setAuthModalOpen] = useAtom(openAuthModalAtom);
  const [isTracked, setIsTracked] = useState(false);
  const [recieveNotifications, setReceiveNotifications] = useState(false);
  const [commentInput, setCommentInput] = useState("");
  const [loading, setLoading] = useState(false);
  const route = useRoute();
  const { isLoggedIn } = useUser();

  useEffect(() => {
    (async function fetchTrackedHorse() {
      if (!isExpanded) return;
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }
      try {
        if (trackedHorse || route.name === "IndividualRacecard") {
          setIsTracked(!!trackedHorse);
          setCommentInput(trackedHorse?.tracked_horse.comment || "");
          setReceiveNotifications(
            trackedHorse?.tracked_horse?.notifications || false
          );
        } else {
          const { meta, horses } = await getTrackedHorsesByIds([horseId]);
          if (meta.status !== 200) {
            throw new Error(`Tracker was not updated`);
          }

          // TODO: Create a new API endpoint to fetch a single tracked horse
          const fetchedHorse = horses[0];
          if (!fetchedHorse) return;

          setIsTracked(true);
          setCommentInput(fetchedHorse.tracked_horse.comment ?? "");
          setReceiveNotifications(fetchedHorse.tracked_horse.notifications);
        }
      } catch (error) {
        return { isTracked: false, fetchedHorse: null, error: error };
      } finally {
        setLoading(false);
      }
    })();
  }, [isExpanded, trackedHorse]);

  const handleTrackHorsePress = async () => {
    if (!isLoggedIn) {
      setAuthModalOpen("login");
      return;
    }
    if (isTracked) {
      const success = await handleDeleteTrackedHorse({
        id: horseId,
        name: horseName,
        toast,
      });
      if (success) {
        setIsTracked(false);
        setReceiveNotifications(false);
        setCommentInput("");
      }
    } else {
      const {
        meta: { status },
      } = await handleUpdateTrackedHorse({
        id: horseId,
        name: horseName,
        toast,
        comment: commentInput,
        notifications: true,
      });

      if (status === 200) {
        setIsTracked(true);
        setReceiveNotifications(true);
        setCommentInput("");
      }
    }
  };

  const handleNotificationOrSavePress = async (
    pressType: "save" | "notification"
  ) => {
    if (!isLoggedIn) {
      setAuthModalOpen("login");
      return;
    }
    const notifications =
      pressType === "notification"
        ? !recieveNotifications
        : recieveNotifications;

    if (isTracked) {
      const {
        meta: { status },
      } = await handleUpdateTrackedHorse({
        id: horseId,
        name: horseName,
        toast,
        comment: commentInput,
        notifications,
      });

      if (status === 200) {
        setReceiveNotifications(!recieveNotifications);
      }
    } else {
      const {
        meta: { status },
      } = await handleUpdateTrackedHorse({
        id: horseId,
        name: horseName,
        toast,
        comment: commentInput,
        notifications: true,
      });

      if (status === 200) {
        setIsTracked(true);
        setReceiveNotifications(true);
        if (pressType === "notification") setCommentInput("");
      }
    }
  };

  if (loading)
    return (
      <View
        style={[
          styles.commentContainer,
          isTracked ? styles.trackedComment : styles.notTrackedComment,
          {
            height: isExpanded ? 100 : 0,
            marginTop: isExpanded ? 5 : 0,
          },
        ]}
      >
        <TextLoader height={isExpanded ? 100 : 0} />
      </View>
    );

  if (!isExpanded) return null;

  return (
    <View
      style={{
        backgroundColor: variables.colors.palette.neutral.x100,
        padding: 10,
        borderRadius: 2,
      }}
    >
      <View
        style={[
          styles.commentContainer,
          isTracked ? styles.trackedComment : styles.notTrackedComment,
        ]}
      >
        <RTextInput
          keyboardType="default"
          placeholder="Tracker comment"
          onChangeText={(e) => setCommentInput(e)}
          value={commentInput}
          style={styles.comment}
          onBlur={() => null}
          isSecure={false}
          multiline
        />
      </View>
      <View
        style={[
          styles.commentCtas,
          !withControls && { justifyContent: "flex-end" },
        ]}
      >
        {withControls && (
          <View
            style={{
              flexDirection: "row",
              justifyContent: "flex-start",
              width: "75%",
            }}
          >
            <TrackerButton
              text="Track Horse"
              iconName={
                isTracked ? "binocularsSelected" : "binocularsUnselected"
              }
              onPress={handleTrackHorsePress}
            />
            <Gap size="medium" />
            <TrackerButton
              text="Notifications"
              iconName={
                recieveNotifications
                  ? "notificationSelected"
                  : "notificationUnselected"
              }
              onPress={() => handleNotificationOrSavePress("notification")}
            />
          </View>
        )}
        <TouchableOpacity
          disabled={commentInput?.length === 0}
          activeOpacity={0.6}
          style={{
            marginRight: 4,
          }}
          onPress={() => handleNotificationOrSavePress("save")}
        >
          <RText
            capitalize
            color={commentInput?.length === 0 ? "grey" : "secondary"}
            weight="bold"
          >
            save
          </RText>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const { colors } = variables;
const styles = StyleSheet.create({
  commentContainer: {
    justifyContent: "space-between",
    width: "100%",
    borderRadius: 2,
    backgroundColor: colors.palette.transparent.secondary,
  },
  comment: {
    width: "100%",
    borderWidth: 0,
  },
  trackedComment: {
    backgroundColor: colors.palette.rtv.tracker,
  },
  notTrackedComment: {
    backgroundColor: "white",
  },
  commentCtas: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: variables.spacing.xsmall,
  },
});
